import React, { useState, useEffect, useMemo } from "react";
import {
    Container,
    Card,
    CardBody,
    CardTitle,
    Button,
    Table,
    Input,
    Label,
    FormGroup,
    ListGroup,
    ListGroupItem,
    Modal,
    ModalHeader,
    ModalBody,
} from "reactstrap";
import DriverForm from "./DriverForm";
import Session from '../auth/Session';
import useSortableData from "./useSortableData";
import SortableHeader from "./SortableHeader";

const normalizeState = (value) => String(value || "").trim().toUpperCase();

const getDriverStates = (driver) => {
    const states = [
        ...(driver?.states || []),
        ...(driver?.stateAssignments || []).map(a => a.state),
    ];

    if (!states.length && driver?.state) {
        states.push(driver.state);
    }

    return [...new Set(states.map(normalizeState).filter(Boolean))].sort();
};

const displayDriverStates = (driver) => getDriverStates(driver).join(", ");

const DriverGrid = () => {
    const apiBaseUrl = "/api/drivers";
    const driverListApi = "/api/drivers/list";
    const customersApi = "/api/customers";

    const [drivers, setDrivers] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [filter, setFilter] = useState("");
    const [filteredCustomers, setFilteredCustomers] = useState([]);
    const [showCustomerList, setShowCustomerList] = useState(false);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [editing, setEditing] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [includeInactive, setIncludeInactive] = useState(false);

    const sortGetters = useMemo(() => ({ routeStates: displayDriverStates }), []);

    const fetchDrivers = async (customerId = null) => {
        let token = await Session.getToken();
        const base = customerId ? `${apiBaseUrl}/ByCustomer/${customerId}/list` : driverListApi;
        const url = `${base}?includeInactive=${includeInactive}`;
        const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
        const data = await res.json();
        setDrivers(data);
    };

    const handleOpenEdit = async (driver) => {
        const token = await Session.getToken();
        const res = await fetch(`${apiBaseUrl}/${driver.id}`, { headers: { Authorization: `Bearer ${token}` } });
        setEditing(res.ok ? await res.json() : driver);
        setModalOpen(true);
    };

    useEffect(() => {
        fetchDrivers(selectedCustomer?.id);
    }, [includeInactive]);

    const filteredDrivers = useMemo(() => {
        if (filter === '') return drivers;
        const search = filter.toLowerCase();
        return drivers.filter(d => d.businessName?.toLowerCase().includes(search) ||
            d.driverName?.toLowerCase().includes(search) ||
            d.email?.toLowerCase().includes(search) ||
            d.phone?.includes(search) ||
            d.city?.toLowerCase().includes(search) ||
            d.state?.toLowerCase().includes(search) ||
            displayDriverStates(d).toLowerCase().includes(search));
    }, [drivers, filter]);

    const { items: sortedDrivers, sortConfig, requestSort } = useSortableData(
        filteredDrivers,
        { key: 'driverName', direction: 'asc', getters: sortGetters }
    );

    const handleOpenNew = async () => {
        setEditing(null);
        setModalOpen(true);
    }
    //const handleCustomerSelect = (c) => {
    //    setSelectedCustomer(c);
    //    setShowCustomerList(false);
    //    fetchDrivers(c.id);
    //};

    return (
        <Container className="mt-5">
            <Card>
                <CardBody>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <CardTitle tag="h5">Driver Management</CardTitle>
                        <Button
                            color="primary"
                            onClick={handleOpenNew}
                        >
                            + New Driver
                        </Button>

                    </div>

                    <div className="mb-3 position-relative d-flex align-items-center gap-3">
                        <Input
                            placeholder="Search Drivers..."
                            value={filter}
                            onChange={(e) => {
                                setFilter(e.target.value);
                            }}
                        />
                        <FormGroup check className="mb-0 text-nowrap">
                            <Label check>
                                <Input
                                    id="includeInactive"
                                    type="checkbox"
                                    checked={includeInactive}
                                    onChange={(e) => setIncludeInactive(e.target.checked)}
                                />{" "}
                                Show inactive drivers
                            </Label>
                        </FormGroup>
                    </div>

                    <Table bordered hover responsive>
                        <thead className="table-light">
                            <tr>
                                <SortableHeader label="Driver" sortKey="driverName" sortConfig={sortConfig} onSort={requestSort} />
                                <SortableHeader label="Business" sortKey="businessName" sortConfig={sortConfig} onSort={requestSort} />
                                <SortableHeader label="Email" sortKey="email" sortConfig={sortConfig} onSort={requestSort} />
                                <SortableHeader label="Phone" sortKey="phone" sortConfig={sortConfig} onSort={requestSort} />
                                <SortableHeader label="City" sortKey="city" sortConfig={sortConfig} onSort={requestSort} />
                                <SortableHeader label="State" sortKey="state" sortConfig={sortConfig} onSort={requestSort} />
                                <SortableHeader label="Route States" sortKey="routeStates" sortConfig={sortConfig} onSort={requestSort} />
                                <SortableHeader label="End Date" sortKey="endDate" sortConfig={sortConfig} onSort={requestSort} />
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {sortedDrivers.length === 0 ? (
                                <tr>
                                    <td colSpan="9" className="text-center">
                                        No drivers found
                                    </td>
                                </tr>
                            ) : (
                                sortedDrivers.map((d) => (
                                    <tr key={d.id} className={d.active === false ? 'text-muted fst-italic' : ''}>
                                        <td>{d.driverName}</td>
                                        <td>{d.businessName}</td>
                                        <td>{d.email}</td>
                                        <td>{d.phone}</td>
                                        <td>{d.city}</td>
                                        <td>{d.state}</td>
                                        <td>{displayDriverStates(d)}</td>
                                        <td>{d.endDate ? String(d.endDate).slice(0, 10) : ""}</td>
                                        <td className="text-end">
                                            <Button size="sm" color="info" onClick={() => handleOpenEdit(d)}>
                                                Edit
                                            </Button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </Table>
                </CardBody>
            </Card>

            <Modal isOpen={modalOpen} toggle={() => setModalOpen(false)} size="lg">
                <ModalHeader toggle={() => setModalOpen(false)}>
                    {editing ? "Edit Driver" : "New Driver"}
                </ModalHeader>
                <ModalBody>
                    <DriverForm
                        driver={editing}
                        apiBaseUrl={apiBaseUrl}
                        onSuccess={() => { setModalOpen(false); fetchDrivers(selectedCustomer?.id); }}
                        onCancel={() => setModalOpen(false)}
                    />
                </ModalBody>
            </Modal>
        </Container>
    );
};

export default DriverGrid;
